import { SyntheticEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Autocomplete, AutocompleteProps, TextField } from '@mui/material'
import { Company, RentalCompany } from '../types'
import { getCompanies, getRentalCompanies } from '../services/companyService'
import { getProjects } from '../services/projectService'

export type RentalCompanyAutocompleteProps = Omit<
  AutocompleteProps<RentalCompany | Company, false, false, false>,
  'options' | 'value' | 'getOptionLabel' | 'getOptionKey' | 'renderInput' | 'onChange'
> & {
  name?: string
  label?: string
  value: number | null
  onChange?: (event: SyntheticEvent, value: number | null) => void
}

export default function RentalCompanyAutocomplete({
  name,
  label,
  disabled,
  value,
  inputValue,
  onChange,
  ...props
}: RentalCompanyAutocompleteProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation()

  /**
   * The options state.
   */
  const [options, setOptions] = useState<Array<RentalCompany | Company>>([])

  /**
   * The loading state.
   */
  const [isLoading, setLoading] = useState<boolean>(true)

  /**
   * Load the options.
   */
  async function loadRentalCompanies(controller?: AbortController) {
    try {
      setLoading(true)

      const rentalCompanies = await getRentalCompanies({ signal: controller?.signal })

      // NOTE: We could also, try to get the user's company and companies of one's projects via
      // auth state.
      const userCompanies = [
        ...(await getCompanies({}, { signal: controller?.signal })),
        ...(await getProjects({}, { signal: controller?.signal }))
          .filter((project) => Boolean(project?.company))
          .map((project) => project.company as Company)
      ]

      // Remove duplicates by ID.
      const options = [...rentalCompanies, ...userCompanies]
        .filter((company, index, self) => index === self.findIndex((c) => c.id === company.id))
        .sort((a, b) => a.name.localeCompare(b.name))

      setOptions(options)
    } catch (_) {
      // FIXME: Handle the error.
    } finally {
      setLoading(false)
    }
  }

  /**
   * Handle the clear selection.
   */
  function handleChange(event: SyntheticEvent, value: RentalCompany | null) {
    onChange?.(event, value?.id ?? null)
  }

  /**
   * Load the rental companies when the component mounts unless options are provided.
   */
  useEffect(() => {
    const controller = new AbortController()

    loadRentalCompanies(controller)

    return () => controller.abort()
  }, [])

  return (
    <Autocomplete
      fullWidth
      options={options}
      getOptionLabel={(option) => option.name}
      getOptionKey={(option) => option.id}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      disabled={disabled || isLoading}
      {...props}
      renderInput={(params) => (
        <TextField
          name={name ?? 'rentalCompany'}
          label={label ?? t('rentalCompany')}
          value={inputValue ?? options.find((option) => option.id === value)?.name ?? ''}
          {...params}
        />
      )}
      value={options.find((option) => option.id === value) ?? null}
      onChange={handleChange}
    />
  )
}
